import React, { useState, useContext, useEffect } from "react";
import { ModelCxContext } from "../context/modelCx.context";
import { UserChartModal } from "../components/UserChartModal";
import { QueryPerformanceModal } from "../components/QueryPerformanceModal";

export const LogsScreen = () => {
  const [statusFilter, setStatusFilter] = useState("all");
  const [isPolling, setIsPolling] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedQueries, setExpandedQueries] = useState({});
  const [pagination, setPagination] = useState([]);
  const [logsQueryTimes, setLogsQueryTimes] = useState([]); // Track history of query execution times
  const [totalQueryCount, setTotalQueryCount] = useState(0); // Track total number of queries
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [isPerformanceModalOpen, setIsPerformanceModalOpen] = useState(false);
  const {
    awsgwLogsResult,
    // (#79) awsgwGetLogs not destructured — refetch goes through setShouldFetchLogs
    setShouldFetchLogs,
    logsFetching,
    queryExecutionTimes = {},
    logsUserFilter,
    logsDateFilter,
    logsDaysFilter,
    setLogsUserFilter,
    setLogsDateFilter,
    setLogsDaysFilter,
    logsPage,
    logsPageSize,
    logsSortColumn,
    logsSortDirection,
    logsTotalCount,
    setLogsPage,
    setLogsPageSize,
    setLogsSortColumn,
    setLogsSortDirection,
    logsUniqueUsersResult,
  } = useContext(ModelCxContext);

  // Track logs query execution times
  useEffect(() => {
    if (queryExecutionTimes?.getLogs) {
      setLogsQueryTimes(prev => {
        const newTimes = [...prev, queryExecutionTimes.getLogs];
        // Keep only last 20 measurements
        return newTimes.slice(-20);
      });
      setTotalQueryCount(prev => prev + 1);
    }
  }, [queryExecutionTimes?.getLogs]);

  // Calculate average query time
  const avgQueryTime = logsQueryTimes.length > 0
    ? Math.round(logsQueryTimes.reduce((sum, time) => sum + time, 0) / logsQueryTimes.length)
    : 0;
  const lastQueryTime = logsQueryTimes.length > 0 ? logsQueryTimes[logsQueryTimes.length - 1] : 0;

  // Calculate data size
  const dataSize = awsgwLogsResult ? new Blob([JSON.stringify(awsgwLogsResult)]).size : 0;
  const dataSizeFormatted = dataSize < 1024 * 1024 
    ? `${(dataSize / 1024).toFixed(1)}KB` 
    : `${(dataSize / (1024 * 1024)).toFixed(2)}MB`;

  // Check if any rows or queries are expanded
  // Pause polling when viewing any details to prevent data shift changing what you're looking at
  const hasExpandedRows = Object.keys(expandedRows).some(key => expandedRows[key]);
  const hasExpandedQueries = Object.keys(expandedQueries).some(key => expandedQueries[key]);
  const isViewingDetails = hasExpandedRows || hasExpandedQueries;

  // Polling effect - only enable when on page 1, no expanded rows, and polling is on
  useEffect(() => {
    // shouldFetchLogs controls refetchInterval (polling), not query enabled state
    // Query is always enabled to allow manual refetches on filter/sort changes
    // Disable polling when user is viewing expanded details to prevent data shift
    if (isPolling && logsPage === 1 && !isViewingDetails) {
      setShouldFetchLogs(true); // Enable 10s polling on page 1
    } else {
      setShouldFetchLogs(false); // Disable polling (but query still runs on demand)
    }
  }, [isPolling, logsPage, isViewingDetails, setShouldFetchLogs]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setLogsPage(1);
  }, [logsUserFilter, logsDateFilter, logsDaysFilter, setLogsPage]);

  // Helper function to show why auto-refresh is disabled
  const getDisabledReason = () => {
    if (logsPage !== 1) return 'Paused (Page 2+)';
    if (isViewingDetails) return 'Paused (Viewing)';
    return 'ON (10s)';
  };

  // Calculate pagination array
  useEffect(() => {
    let newPagination = [];
    const totalCount = Math.ceil(logsTotalCount / logsPageSize);
    if (totalCount < 11) {
      newPagination = Array(totalCount)
        .fill(null)
        .map((_, index) => {
          return index + 1;
        });
    } else {
      // Always print first page button
      newPagination.push(1);

      // Print "..." only if currentPage is > 6
      if (logsPage > 6) {
        newPagination.push("...");
      }

      // Print previous number button
      if (logsPage > 6 && logsPage < totalCount - 4) {
        Array(3)
          .fill(null)
          .forEach((_, index) => {
            newPagination.push(logsPage - 3 + index);
          });
      }

      if (logsPage > 2 && logsPage < 7) {
        Array(logsPage - 2)
          .fill(null)
          .forEach((_, index) => {
            newPagination.push(2 + index);
          });
      }

      if (logsPage > 6 && logsPage > totalCount - 5) {
        Array(logsPage - (totalCount - 7))
          .fill(null)
          .forEach((_, index) => {
            newPagination.push(totalCount - 7 + index);
          });
      }

      //Print current page number button as long as it not the first or last page
      if (logsPage !== 1 && logsPage !== totalCount) {
        newPagination.push(logsPage);
      }

      //print next number button
      if (logsPage < totalCount - 4 && logsPage > 5) {
        Array(2)
          .fill(null)
          .forEach((_, index) => {
            newPagination.push(logsPage + 1 + index);
          });
      }
      if (logsPage < totalCount - 1 && logsPage < 6 && logsPage !== 1) {
        Array(8 - logsPage)
          .fill(null)
          .forEach((_, index) => {
            newPagination.push(logsPage + 1 + index);
          });
      }

      if (logsPage < totalCount - 1 && logsPage > totalCount - 5) {
        Array(totalCount - logsPage - 1)
          .fill(null)
          .forEach((_, index) => {
            newPagination.push(logsPage + 1 + index);
          });
      }

      // special case where first page is selected...
      if (logsPage === 1) {
        Array(7)
          .fill(null)
          .forEach((_, index) => {
            newPagination.push(logsPage + 1 + index);
          });
      }

      // print "..." if currentPage is < lastPage - 4
      if (logsPage < totalCount - 4) {
        newPagination.push("...");
      }

      //Always print last page button if there is more than 1 page
      newPagination.push(totalCount);
    }
    setPagination(newPagination);
  }, [logsPage, logsPageSize, logsTotalCount]);

  const handleSort = (column) => {
    if (logsFetching) return; // Don't allow sorting while fetching
    
    if (logsSortColumn === column) {
      // Toggle direction if clicking the same column
      setLogsSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      // New column, default to ascending
      setLogsSortColumn(column);
      setLogsSortDirection("asc");
    }
    // Reset to page 1 when sorting changes
    setLogsPage(1);
  };

  const toggleRow = (index) => {
    setExpandedRows(prev => {
      const isCurrentlyExpanded = prev[index];

      // If collapsing the row, also collapse all its child queries
      if (isCurrentlyExpanded) {
        setExpandedQueries(prevQueries => {
          const newQueries = { ...prevQueries };
          // Find and remove all query keys for this row (format: "rowIndex-queryIndex")
          Object.keys(newQueries).forEach(key => {
            if (key.startsWith(`${index}-`)) {
              delete newQueries[key];
            }
          });
          return newQueries;
        });
      }

      return {
        ...prev,
        [index]: !prev[index]
      };
    });
  };

  const toggleQuery = (rowIndex, queryIndex) => {
    const key = `${rowIndex}-${queryIndex}`;
    setExpandedQueries(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Get unique users from dedicated query (all users who have ever created logs)
  // Falls back to current log entries if the query hasn't loaded yet
  const uniqueUsers = logsUniqueUsersResult && logsUniqueUsersResult.length > 0
    ? ['all', ...logsUniqueUsersResult]
    : awsgwLogsResult
      ? ['all', ...Array.from(new Set(awsgwLogsResult.map(entry => entry.user).filter(Boolean))).sort((a, b) => a.localeCompare(b))]
      : ['all'];

  // Filter logs by status (client-side only - user/date/days filters handled server-side)
  let filteredLogs = awsgwLogsResult || [];
  
  if (statusFilter !== 'all') {
    filteredLogs = filteredLogs.filter(entry => {
      // Polling entries and entries with no comparison are always "n/a" - exclude from mismatch filter
      const isPollingType = entry.type?.includes('polling');
      const hasNoComparison = entry.queries?.every(q => q.identical === undefined);
      if (isPollingType || hasNoComparison) {
        return false; // Exclude n/a entries from both mismatch and match filters
      }
      const hasMismatch = entry.queries?.some(q => q.identical === false);
      return statusFilter === 'mismatch' ? hasMismatch : !hasMismatch;
    });
  }

  return (
    <div className="flex flex-col px-4 pt-8 gap-4 h-full relative">
      {/* Loading Overlay */}
      {logsFetching && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            zIndex: 9998,
            pointerEvents: "all",
          }}
        />
      )}
      
      {/* Loading Indicator */}
      {logsFetching && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
              padding: "24px 32px",
              backgroundColor: "rgba(0, 0, 0, 0.85)",
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                border: "4px solid rgba(255, 255, 255, 0.2)",
                borderTop: "4px solid #ffffff",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            <div
              style={{
                color: "#ffffff",
                fontSize: "14px",
                fontWeight: "600",
                letterSpacing: "0.5px",
              }}
            >
              Loading logs...
            </div>
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
          </div>
        </div>
      )}
      
      {/* Query Performance Stats - Upper Right */}
      <div className="absolute top-2 right-4 bg-gray-100 border border-gray-300 rounded-sm px-3 py-2 text-xs z-10">
        <div className="font-semibold mb-1 text-gray-700 flex justify-between items-center">
          <span>Logs Query Performance</span>
          <span className="text-gray-500 font-normal ml-3">({logsQueryTimes.length}/20 samples, {totalQueryCount} total)</span>
        </div>
        <div className="flex gap-4">
          <div>
            <span className="text-gray-600">Last:</span>{' '}
            <span className="font-semibold" style={{ color: '#22c55e' }}>{lastQueryTime}ms</span>
          </div>
          <div>
            <span className="text-gray-600">Avg:</span>{' '}
            <span className="font-semibold" style={{ color: '#60a5fa' }}>{avgQueryTime}ms</span>
          </div>
          <div>
            <span className="text-gray-600">Size:</span>{' '}
            <span className="font-semibold" style={{ color: '#c084fc' }}>{dataSizeFormatted}</span>
          </div>
        </div>
      </div>
      
      {/* Filters Row */}
      <div className="flex w-full justify-start gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            className="text-2xl px-2 py-1 rounded-sm transition-colors bg-blue-600 hover:bg-blue-700"
            onClick={() => setIsChartModalOpen(true)}
            title="User Activity Chart"
          >
            📊
          </button>
          <button
            className="text-2xl px-2 py-1 rounded-sm transition-colors bg-purple-600 hover:bg-purple-700"
            onClick={() => setIsPerformanceModalOpen(true)}
            title="Query Performance Chart"
          >
            ⚡
          </button>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-skin-primary">Show last:</span>
          <select
            className="bg-yellow-30 text-black text-sm font-medium px-2 py-1 rounded-sm hover:opacity-80 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ minWidth: "8rem" }}
            value={logsDaysFilter}
            onChange={(e) => setLogsDaysFilter(e.target.value)}
            disabled={logsFetching}
          >
            <option value="1">1 Day</option>
            <option value="3">3 Days</option>
            <option value="7">7 Days</option>
            <option value="30">30 Days</option>
            <option value="all">All Time</option>
          </select>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-skin-primary">User:</span>
          <select
            className="bg-yellow-30 text-black text-sm font-medium px-2 py-1 rounded-sm hover:opacity-80 cursor-pointer w-44 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ minWidth: "11rem" }}
            value={logsUserFilter}
            onChange={(e) => setLogsUserFilter(e.target.value)}
            disabled={logsFetching}
          >
            {uniqueUsers.map((user) => (
              <option key={user} value={user}>
                {user === 'all' ? 'All Users' : user}
              </option>
            ))}
          </select>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-skin-primary">Date:</span>
          <input
            type="date"
            className="bg-yellow-30 text-black text-sm font-medium px-2 py-1 rounded-sm hover:opacity-80 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            value={logsDateFilter}
            onChange={(e) => {
              const newValue = e.target?.value || "";
              setLogsDateFilter(newValue);
            }}
            disabled={logsFetching}
          />
          {logsDateFilter && (
            <button
              className="text-xs px-2 py-1 bg-gray-400 hover:bg-gray-500 text-white rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => setLogsDateFilter("")}
              disabled={logsFetching}
            >
              Clear
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-skin-primary">Status:</span>
          <select
            className="bg-yellow-30 text-black text-sm font-medium px-2 py-1 rounded-sm hover:opacity-80 cursor-pointer w-44 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ minWidth: "11rem" }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            disabled={logsFetching}
          >
            <option value="all">All Status</option>
            <option value="match">All Match</option>
            <option value="mismatch">Has Mismatch</option>
          </select>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-skin-primary">Auto-Refresh:</span>
          <button
            className={`text-sm font-medium px-3 py-1 rounded transition-colors ${
              isPolling
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-300 text-black hover:bg-gray-400'
            }`}
            onClick={() => setIsPolling(!isPolling)}
          >
            {isPolling
              ? (logsFetching ? '● Fetching...' : getDisabledReason())
              : 'OFF'}
          </button>
        </div>
      </div>
      
      {/* Pagination Controls */}
      <div className="flex w-full px-4 py-2 text-sm font-normal bg-skin-primary fill-skin-base items-center">
        <div className="flex-1">
          Showing {filteredLogs.length > 0 ? ((logsPage - 1) * logsPageSize + 1) : 0} to{' '}
          {Math.min(logsPage * logsPageSize, logsTotalCount)} of {logsTotalCount}
          {statusFilter !== 'all' && ' (client-filtered)'}
        </div>
        <div className="flex gap-2 font-semibold">
          <button
            onClick={() => setLogsPage(logsPage - 1)}
            disabled={logsPage === 1 || logsFetching}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="24"
              viewBox="0 0 25 24"
            >
              <path d="M15.9547 5.46394L9.41396 12.0047L15.9547 18.5454C16.3452 18.9359 16.3452 19.5691 15.9547 19.9596C15.5641 20.3501 14.931 20.3501 14.5405 19.9596L7.29266 12.7118C7.10506 12.5242 6.99976 12.2699 6.99976 12.0047C6.99976 11.7395 7.10506 11.4851 7.29266 11.2976L14.5405 4.04973C14.5893 4.00091 14.6419 3.9582 14.6973 3.92159C15.0855 3.66531 15.613 3.70802 15.9547 4.04973C16.3452 4.44025 16.3452 5.07342 15.9547 5.46394Z" />
            </svg>
          </button>
          {pagination.map((page, index) => (
            <button
              onClick={() => typeof page === 'number' && setLogsPage(page)}
              className={`${
                page === logsPage
                  ? "text-yellow border-b border-b-yellow-30"
                  : ""
              } min-w-4`}
              key={index}
              disabled={typeof page !== 'number' || logsFetching}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => setLogsPage(logsPage + 1)}
            disabled={logsPage * logsPageSize >= logsTotalCount || logsFetching}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="24"
              viewBox="0 0 25 24"
            >
              <path d="M9.04533 18.5361L15.586 11.9953L9.04533 5.4546C8.65481 5.0641 8.65481 4.4309 9.04533 4.0404C9.43586 3.6499 10.069 3.6499 10.4595 4.0404L17.7073 11.2882C17.8949 11.4758 18.0002 11.7301 18.0002 11.9953C18.0002 12.2605 17.8949 12.5149 17.7073 12.7024L10.4595 19.9503C10.4107 19.9991 10.3581 20.0418 10.3027 20.0784C9.91452 20.3347 9.38704 20.292 9.04533 19.9503C8.65481 19.5598 8.65481 18.9266 9.04533 18.5361Z" />
            </svg>
          </button>
        </div>
        <div className="flex-1 flex justify-end items-center gap-2">
          Rows per Page:{" "}
          <select
            id="page-size"
            className="bg-yellow-30 text-black text-sm font-medium hover:opacity-75 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            onChange={(event) => setLogsPageSize(parseInt(event.target.value))}
            value={logsPageSize}
            disabled={logsFetching}
          >
            {[10, 20, 50, 100].map((count, index) => (
              <option value={count} key={index}>
                {count}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Log Entries Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="text-left text-xs font-semibold py-1 px-2 border-b w-8"></th>
              <th 
                className={`text-left text-xs font-semibold py-1 px-2 border-b ${logsFetching ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-300'}`}
                onClick={() => handleSort("when")}
              >
                When {logsSortColumn === "when" && (logsSortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className={`text-left text-xs font-semibold py-1 px-2 border-b ${logsFetching ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-300'}`}
                onClick={() => handleSort("user")}
              >
                User {logsSortColumn === "user" && (logsSortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className={`text-left text-xs font-semibold py-1 px-2 border-b ${logsFetching ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-300'}`}
                onClick={() => handleSort("type")}
              >
                Type {logsSortColumn === "type" && (logsSortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className={`text-left text-xs font-semibold py-1 px-2 border-b ${logsFetching ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-300'}`}
                onClick={() => handleSort("status")}
              >
                Status {logsSortColumn === "status" && (logsSortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className={`text-left text-xs font-semibold py-1 px-2 border-b ${logsFetching ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-300'}`}
                onClick={() => handleSort("tanstack")}
              >
                TanStack(max) {logsSortColumn === "tanstack" && (logsSortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className={`text-left text-xs font-semibold py-1 px-2 border-b ${logsFetching ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-300'}`}
                onClick={() => handleSort("realm")}
              >
                Realm {logsSortColumn === "realm" && (logsSortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className={`text-left text-xs font-semibold py-1 px-2 border-b ${logsFetching ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-300'}`}
                onClick={() => handleSort("payload")}
              >
                Payload Size {logsSortColumn === "payload" && (logsSortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className={`text-left text-xs font-semibold py-1 px-2 border-b ${logsFetching ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-300'}`}
                onClick={() => handleSort("capacity")}
              >
                Capacity % {logsSortColumn === "capacity" && (logsSortDirection === "asc" ? "↑" : "↓")}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs && filteredLogs.length > 0 ? (
              filteredLogs.map((entry, index) => {
                  // Check if any query has a mismatch
                  const hasMismatch = entry.queries?.some(q => q.identical === false);
                  // Check if this is a polling log (comparison not applicable)
                  // Polling types: polling-batch, polling-partial, attributes-polling-batch, attributes-polling-partial
                  // For these types, comparison is either not done (tanstackOnly) or not meant for evaluation (both)
                  const isPollingType = entry.type?.includes('polling');
                  // Also check if all queries have undefined identical (for tanstackOnly initial-load entries)
                  const hasNoComparison = entry.queries?.every(q => q.identical === undefined);
                  const showNotApplicable = isPollingType || hasNoComparison;

                  // Find query with max TanStack execution time (excluding logs and users queries)
                  let maxTanStackQuery = null;
                  if (entry.queries && entry.queries.length > 0) {
                    maxTanStackQuery = entry.queries
                      .filter(query => query.query !== 'logs' && query.query !== 'users')
                      .reduce((max, query) => {
                        const tanstackTime = query.tanstackExecutionTimeMs ?? 0;
                        const maxTime = max?.tanstackExecutionTimeMs ?? 0;
                        return tanstackTime > maxTime ? query : max;
                      }, null);
                  }
                  
                  // Find query with largest payload size (all queries included)
                  let maxPayloadQuery = null;
                  let maxPayloadSize = 0;
                  if (entry.queries && entry.queries.length > 0) {
                    entry.queries.forEach(query => {
                      const payloadSize = query.payloadSizeBytes ?? 0;
                      if (payloadSize > maxPayloadSize) {
                        maxPayloadSize = payloadSize;
                        maxPayloadQuery = query;
                      }
                    });
                  }
                  
                  // Determine payload color based on size (6MB limit)
                  const getPayloadColor = (sizeBytes) => {
                    const sizeMB = sizeBytes / (1024 * 1024);
                    if (sizeMB >= 5) return '#ef4444'; // red for >= 5MB
                    if (sizeMB >= 4) return '#f59e0b'; // yellow/orange for >= 4MB
                    return '#000000'; // black for normal
                  };
                  
                  // Find query with highest capacity percentage
                  let maxCapacityQuery = null;
                  let maxCapacityPercent = 0;
                  if (entry.queries && entry.queries.length > 0) {
                    entry.queries.forEach(query => {
                      const capacityPercent = query.capacityPercent ?? 0;
                      if (capacityPercent > maxCapacityPercent) {
                        maxCapacityPercent = capacityPercent;
                        maxCapacityQuery = query;
                      }
                    });
                  }
                  
                  // Determine capacity color based on percentage
                  const getCapacityColor = (percent) => {
                    if (percent >= 90) return '#ef4444'; // red for >= 90%
                    if (percent >= 75) return '#f59e0b'; // yellow/orange for >= 75%
                    return '#000000'; // black for normal
                  };
                  
                  return (
                <React.Fragment key={index}>
                  <tr 
                    className="border-b hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleRow(index)}
                  >
                    <td className="text-center text-black text-xs py-1 px-2">
                      <span className="inline-block w-4">
                        {expandedRows[index] ? '▼' : '▶'}
                      </span>
                    </td>
                    <td className="text-left text-black text-xs py-1 px-2">
                      {entry.when}
                    </td>
                    <td className="text-left text-black text-xs py-1 px-2">
                      {entry.user}
                    </td>
                    <td className="text-left text-black text-xs py-1 px-2">
                      {entry.type}
                    </td>
                    <td className="text-left text-xs py-1 px-2">
                      {showNotApplicable ? (
                        <span className="text-gray-500">n/a</span>
                      ) : hasMismatch ? (
                        <span style={{ color: '#f87171' }} className="font-semibold">✗ Mismatch</span>
                      ) : (
                        <span style={{ color: '#22c55e' }} className="font-semibold">✓ All Match</span>
                      )}
                    </td>
                    <td className="text-left text-xs py-1 px-2">
                      {maxTanStackQuery && maxTanStackQuery.tanstackExecutionTimeMs !== null && maxTanStackQuery.tanstackExecutionTimeMs !== undefined
                        ? `${Math.round(maxTanStackQuery.tanstackExecutionTimeMs)}ms`
                        : 'n/a'
                      }
                    </td>
                    <td className="text-left text-xs py-1 px-2">
                      {maxTanStackQuery && maxTanStackQuery.realmExecutionTimeMs !== null && maxTanStackQuery.realmExecutionTimeMs !== undefined
                        ? `${Math.round(maxTanStackQuery.realmExecutionTimeMs)}ms`
                        : 'n/a'
                      }
                    </td>
                    <td className="text-left text-xs py-1 px-2">
                      {maxPayloadQuery && maxPayloadQuery.payloadSizeBytes !== null && maxPayloadQuery.payloadSizeBytes !== undefined
                        ? (
                          <span style={{ color: getPayloadColor(maxPayloadQuery.payloadSizeBytes), fontWeight: maxPayloadQuery.payloadSizeBytes >= 4 * 1024 * 1024 ? 'bold' : 'normal' }}>
                            {maxPayloadQuery.payloadSizeBytes === 0
                              ? '0 B'
                              : maxPayloadQuery.payloadSizeBytes < 1024
                              ? `${maxPayloadQuery.payloadSizeBytes} B`
                              : maxPayloadQuery.payloadSizeBytes < 1024 * 1024
                              ? `${(maxPayloadQuery.payloadSizeBytes / 1024).toFixed(2)} KB`
                              : `${(maxPayloadQuery.payloadSizeBytes / (1024 * 1024)).toFixed(2)} MB`}
                          </span>
                        )
                        : 'n/a'
                      }
                    </td>
                    <td className="text-left text-xs py-1 px-2">
                      {maxCapacityQuery && maxCapacityQuery.capacityPercent !== null && maxCapacityQuery.capacityPercent !== undefined
                        ? (
                          <span style={{ color: getCapacityColor(maxCapacityQuery.capacityPercent), fontWeight: maxCapacityQuery.capacityPercent >= 75 ? 'bold' : 'normal' }}>
                            {maxCapacityQuery.capacityPercent}%
                          </span>
                        )
                        : 'n/a'
                      }
                    </td>
                  </tr>
                  {expandedRows[index] && (
                    <tr>
                      <td colSpan="9" className="bg-gray-50 p-2">
                        <div className="space-y-2">
                          {/* Polling Batch Comparison Summary (for polling-batch logs with comparison data) */}
                          {entry.type === 'polling-batch' && entry.comparison && (
                            <div className="flex gap-4 text-xs mb-3 p-2 rounded-sm" style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}>
                              <div className="font-semibold text-gray-700">Polling Batch Comparison:</div>
                              <div>
                                <span className="text-gray-600">Polls:</span>{' '}
                                <span className="font-semibold">{entry.pollCount}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Matched:</span>{' '}
                                <span className="font-semibold" style={{ color: '#22c55e' }}>{entry.comparison.identicalCount || 0}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Mismatched:</span>{' '}
                                <span className="font-semibold" style={{ color: entry.comparison.mismatchCount > 0 ? '#ef4444' : '#6b7280' }}>
                                  {entry.comparison.mismatchCount || 0}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">Last Realm:</span>{' '}
                                <span className="font-semibold">{entry.comparison.lastRealmCount ?? 'n/a'}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Last TanStack:</span>{' '}
                                <span className="font-semibold">{entry.comparison.lastTanstackCount ?? 'n/a'}</span>
                              </div>
                              {entry.comparison.lastNote && (
                                <div>
                                  <span className="text-gray-600">Note:</span>{' '}
                                  <span className="font-semibold">{entry.comparison.lastNote}</span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Expected/Completed/Missing Queries Summary */}
                          <div className="flex gap-4 text-xs mb-3">
                            {entry.expectedQueries && (
                              <div>
                                <span className="font-semibold">Expected:</span> {entry.expectedQueries.join(', ')}
                              </div>
                            )}
                            {entry.completedQueries && (
                              <div>
                                <span className="font-semibold">Completed:</span> {entry.completedQueries.join(', ')}
                              </div>
                            )}
                            {entry.missingQueries && entry.missingQueries.length > 0 && (
                              <div className="text-red-600">
                                <span className="font-semibold">Missing:</span> {entry.missingQueries.join(', ')}
                              </div>
                            )}
                          </div>
                          
                          {/* Queries Array */}
                          {entry.queries && entry.queries.length > 0 && (
                            <div>
                              <div className="font-semibold text-xs mb-1">Queries ({entry.queries.length}):</div>
                              <div className="space-y-1">
                                {entry.queries.map((query, qIndex) => (
                                  <div key={qIndex} className="border border-gray-300 rounded-sm">
                                    <div 
                                      className="bg-gray-100 px-2 py-1 cursor-pointer hover:bg-gray-200 flex justify-between items-center"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleQuery(index, qIndex);
                                      }}
                                    >
                                      <div className="flex items-center gap-2 shrink-0" style={{ minWidth: '200px' }}>
                                        <span className="inline-block w-4 text-xs">
                                          {expandedQueries[`${index}-${qIndex}`] ? '▼' : '▶'}
                                        </span>
                                        <span className="font-semibold text-xs">{query.query || query.queryName}</span>
                                      </div>
                                      <div className="flex gap-3 text-xs">
                                        <span className="text-green-700" style={{ minWidth: '120px' }}>
                                          {(query.tanstackExecutionTimeMs !== undefined && query.tanstackExecutionTimeMs !== null) 
                                            ? `TanStack: ${Math.round(query.tanstackExecutionTimeMs)}ms`
                                            : 'TanStack: n/a'
                                          }
                                        </span>
                                        <span className="text-blue-700" style={{ minWidth: '100px' }}>
                                          {query.realmExecutionTimeMs !== undefined && query.realmExecutionTimeMs !== null 
                                            ? `Realm: ${Math.round(query.realmExecutionTimeMs)}ms`
                                            : 'Realm: n/a'
                                          }
                                        </span>
                                        <span className="text-purple-700" style={{ minWidth: '100px' }}>
                                          {query.payloadSizeBytes !== undefined && query.payloadSizeBytes !== null
                                            ? query.payloadSizeBytes === 0
                                              ? '0 B'
                                              : query.payloadSizeBytes < 1024
                                              ? `${query.payloadSizeBytes} B`
                                              : query.payloadSizeBytes < 1024 * 1024
                                              ? `${(query.payloadSizeBytes / 1024).toFixed(2)} KB`
                                              : `${(query.payloadSizeBytes / (1024 * 1024)).toFixed(2)} MB`
                                            : 'Payload: n/a'
                                          }
                                        </span>
                                        <span 
                                          style={{ 
                                            minWidth: '80px',
                                            color: query.capacityPercent >= 90 ? '#ef4444' : query.capacityPercent >= 75 ? '#f59e0b' : '#10b981',
                                            fontWeight: query.capacityPercent >= 75 ? 'bold' : 'normal'
                                          }}
                                        >
                                          {query.capacityPercent !== undefined && query.capacityPercent !== null
                                            ? `${query.capacityPercent}%`
                                            : 'Cap: n/a'
                                          }
                                        </span>
                                        <span
                                          className={showNotApplicable ? "text-gray-500" : query.identical ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}
                                          style={{ minWidth: '80px' }}
                                        >
                                          {showNotApplicable
                                            ? 'n/a'
                                            : query.identical !== undefined
                                              ? (query.identical ? '✓ Match' : '✗ Mismatch')
                                              : ''
                                          }
                                        </span>
                                      </div>
                                    </div>
                                    {expandedQueries[`${index}-${qIndex}`] && (
                                      <div className="p-2 text-xs space-y-1" style={{ backgroundColor: '#e3f2fd' }}>
                                        {query.logs && query.logs.length > 0 ? (
                                          <div>
                                            <div className="font-semibold mb-1" style={{ color: '#000000' }}>Comparison Logs ({query.logs?.length || 0} messages):</div>
                                            <div className="p-2 rounded-sm overflow-x-auto max-h-96 overflow-y-auto border" style={{ backgroundColor: '#ffffff', borderColor: '#cccccc' }}>
                                              {query.logs.map((log, logIndex) => (
                                                <div key={logIndex} className="mb-1 pb-1 border-b last:border-0" style={{ borderColor: '#e0e0e0' }}>
                                                  <div style={{ fontWeight: 'bold', fontSize: '0.75rem', marginBottom: '0.25rem', color: '#0066cc' }}>{log.message || 'No message'}</div>
                                                  {log.data && log.data.length > 0 && (
                                                    <pre className="text-xs whitespace-pre-wrap" style={{ color: '#000000' }}>
                                                      {JSON.stringify(log.data, null, 2)}
                                                    </pre>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        ) : (
                                          <div>
                                            <div className="font-semibold mb-1" style={{ color: '#000000' }}>Query Details:</div>
                                            <div className="p-2 rounded-sm border" style={{ backgroundColor: '#ffffff', borderColor: '#cccccc', color: '#000000' }}>
                                              <div className="grid grid-cols-2 gap-2">
                                                <div style={{ color: '#000000' }}><span className="font-semibold">Query:</span> {query.query || 'n/a'}</div>
                                                <div style={{ color: '#000000' }}><span className="font-semibold">Result:</span> {query.result || 'n/a'}</div>
                                                <div style={{ color: '#000000' }}><span className="font-semibold">TanStack Time:</span> {query.tanstackExecutionTimeMs !== undefined && query.tanstackExecutionTimeMs !== null ? `${Math.round(query.tanstackExecutionTimeMs)}ms` : 'n/a'}</div>
                                                <div style={{ color: '#000000' }}><span className="font-semibold">Realm Time:</span> {query.realmExecutionTimeMs !== undefined && query.realmExecutionTimeMs !== null ? `${Math.round(query.realmExecutionTimeMs)}ms` : 'n/a'}</div>
                                                <div style={{ color: '#000000' }}><span className="font-semibold">Payload:</span> {query.payloadSizeBytes !== undefined && query.payloadSizeBytes !== null ? (
                                                  query.payloadSizeBytes === 0 ? '0 B' :
                                                  query.payloadSizeBytes < 1024 ? `${query.payloadSizeBytes} B` :
                                                  query.payloadSizeBytes < 1024 * 1024 ? `${(query.payloadSizeBytes / 1024).toFixed(2)} KB` :
                                                  `${(query.payloadSizeBytes / (1024 * 1024)).toFixed(2)} MB`
                                                ) : 'n/a'}</div>
                                                <div style={{ color: '#000000' }}><span className="font-semibold">Capacity:</span> {query.capacityPercent !== undefined && query.capacityPercent !== null ? `${query.capacityPercent}%` : 'n/a'}</div>
                                                <div style={{ color: '#000000' }}><span className="font-semibold">Record Count:</span> {query.recordCount !== undefined && query.recordCount !== null ? query.recordCount : 'n/a'}</div>
                                                <div style={{ color: '#000000' }}><span className="font-semibold">Max Limit:</span> {query.maxLimit !== undefined && query.maxLimit !== null ? query.maxLimit : 'n/a'}</div>
                                                {/* Comparison details for polling queries */}
                                                {query.realmCount !== undefined && (
                                                  <div style={{ color: '#000000' }}><span className="font-semibold">Realm Count:</span> {query.realmCount}</div>
                                                )}
                                                {query.tanstackCount !== undefined && (
                                                  <div style={{ color: '#000000' }}><span className="font-semibold">TanStack Count:</span> {query.tanstackCount}</div>
                                                )}
                                                {query.diffCount !== undefined && query.diffCount > 0 && (
                                                  <div style={{ color: '#ef4444' }}><span className="font-semibold">Diff Count:</span> {query.diffCount}</div>
                                                )}
                                                {query.timestamp && (
                                                  <div className="col-span-2" style={{ color: '#000000' }}><span className="font-semibold">Timestamp:</span> {query.timestamp}</div>
                                                )}
                                                {query.note && (
                                                  <div className="col-span-2" style={{ color: '#000000' }}><span className="font-semibold">Note:</span> {query.note}</div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
            ) : (
              <tr>
                <td
                  colSpan="7"
                  className="text-center text-xs py-4 text-dark-grey"
                >
                  No log entries found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* User Chart Modal */}
      <UserChartModal
        isOpen={isChartModalOpen}
        onClose={() => setIsChartModalOpen(false)}
        daysFilter={logsDaysFilter}
        userFilter={logsUserFilter}
      />

      {/* Query Performance Modal */}
      <QueryPerformanceModal
        isOpen={isPerformanceModalOpen}
        onClose={() => setIsPerformanceModalOpen(false)}
      />
    </div>
  );
};
